/**
 * Bitfield de permissões de 64 bits. Ver docs/04-permissions.md.
 *
 * PORQUE `bigint`: os operadores bitwise de JavaScript convertem para int32
 * com sinal. `1 << 32 === 1`. Com um bitfield acima de 31 bits, `number` dá
 * respostas erradas sem avisar — tipicamente uma permissão negada que aparece
 * concedida. Não há exceções a esta regra.
 */

export const P = {
  // Geral
  VIEW_CHANNEL: 1n << 0n,
  CREATE_INVITE: 1n << 1n,
  CHANGE_NICKNAME: 1n << 2n,

  // Mensagens
  SEND_MESSAGES: 1n << 3n,
  SEND_MESSAGES_IN_THREADS: 1n << 4n,
  EMBED_LINKS: 1n << 5n,
  ATTACH_FILES: 1n << 6n,
  ADD_REACTIONS: 1n << 7n,
  USE_EXTERNAL_EMOJIS: 1n << 8n,
  READ_MESSAGE_HISTORY: 1n << 9n,
  MENTION_EVERYONE: 1n << 10n,
  MANAGE_MESSAGES: 1n << 11n,
  CREATE_THREADS: 1n << 12n,
  MANAGE_THREADS: 1n << 13n,

  // Voz
  CONNECT: 1n << 14n,
  SPEAK: 1n << 15n,
  STREAM: 1n << 16n,
  VIDEO: 1n << 17n,
  USE_VAD: 1n << 18n,
  PRIORITY_SPEAKER: 1n << 19n,
  MUTE_MEMBERS: 1n << 20n,
  DEAFEN_MEMBERS: 1n << 21n,
  MOVE_MEMBERS: 1n << 22n,

  // Moderação
  KICK_MEMBERS: 1n << 23n,
  BAN_MEMBERS: 1n << 24n,
  MODERATE_MEMBERS: 1n << 25n,
  MANAGE_NICKNAMES: 1n << 26n,

  // Gestão
  MANAGE_CHANNELS: 1n << 27n,
  MANAGE_ROLES: 1n << 28n,
  MANAGE_GUILD: 1n << 29n,
  MANAGE_EMOJIS: 1n << 30n,
  MANAGE_WEBHOOKS: 1n << 31n,
  VIEW_AUDIT_LOG: 1n << 32n,

  // 33..39 deixados livres de propósito: bits novos de uma categoria
  // existente devem poder ser adicionados junto dos seus pares.

  ADMINISTRATOR: 1n << 40n,
} as const;

export type PermissionName = keyof typeof P;

export const ALL: bigint = Object.values(P).reduce((acc, bit) => acc | bit, 0n);

export const NONE = 0n;

/** Permissões que só fazem sentido dentro de um canal visível. */
export const CHANNEL_SCOPED_MASK: bigint =
  P.SEND_MESSAGES |
  P.SEND_MESSAGES_IN_THREADS |
  P.EMBED_LINKS |
  P.ATTACH_FILES |
  P.ADD_REACTIONS |
  P.USE_EXTERNAL_EMOJIS |
  P.READ_MESSAGE_HISTORY |
  P.MENTION_EVERYONE |
  P.MANAGE_MESSAGES |
  P.CREATE_THREADS |
  P.MANAGE_THREADS |
  P.CONNECT |
  P.SPEAK |
  P.STREAM |
  P.VIDEO |
  P.USE_VAD |
  P.PRIORITY_SPEAKER;

/** O que um membro em timeout mantém. */
export const TIMEOUT_ALLOWED_MASK: bigint = P.VIEW_CHANNEL | P.READ_MESSAGE_HISTORY;

export const DEFAULT_EVERYONE_PERMISSIONS: bigint =
  P.VIEW_CHANNEL |
  P.SEND_MESSAGES |
  P.EMBED_LINKS |
  P.ATTACH_FILES |
  P.ADD_REACTIONS |
  P.USE_EXTERNAL_EMOJIS |
  P.READ_MESSAGE_HISTORY |
  P.CREATE_INVITE |
  P.CHANGE_NICKNAME |
  P.CREATE_THREADS |
  P.SEND_MESSAGES_IN_THREADS |
  P.CONNECT |
  P.SPEAK |
  P.USE_VAD |
  P.STREAM |
  P.VIDEO;

// ─── Tipos de entrada ────────────────────────────────────────────────────────

export type OverwriteTargetType = 'role' | 'member';

export interface Overwrite {
  targetId: bigint;
  targetType: OverwriteTargetType;
  allow: bigint;
  deny: bigint;
}

export interface RoleLike {
  permissions: bigint;
  position: number;
}

export interface GuildLike {
  id: bigint;
  ownerId: bigint;
}

export interface MemberLike {
  userId: bigint;
  roleIds: readonly bigint[];
  /** Quando definido e no futuro, o membro está silenciado por moderação. */
  timeoutUntil?: Date | null;
}

// ─── O algoritmo ─────────────────────────────────────────────────────────────

/**
 * Seis passos, nesta ordem exacta. A ordem É a especificação: trocar dois
 * passos muda quem tem acesso a quê. Ver a tabela de verdade em
 * packages/shared/test/permissions.test.ts.
 *
 * O papel @everyone tem `id === guild.id` (ver docs/02-data-model.md), logo
 * não precisa de linha em member_roles nem de lookup extra.
 */
export function resolvePermissions(
  guild: GuildLike,
  member: MemberLike,
  roles: ReadonlyMap<bigint, RoleLike>,
  overwrites?: readonly Overwrite[],
): bigint {
  // 1. O dono salta tudo. Não é um papel, é uma coluna.
  if (member.userId === guild.ownerId) return ALL;

  // 2. Base = @everyone OR todos os papéis do membro.
  let perms = roles.get(guild.id)?.permissions ?? NONE;
  for (const roleId of member.roleIds) {
    perms |= roles.get(roleId)?.permissions ?? NONE;
  }

  // 3. ADMINISTRATOR a nível de guild concede tudo e ignora overwrites.
  if ((perms & P.ADMINISTRATOR) !== 0n) return applyTimeout(ALL, member);

  if (overwrites && overwrites.length > 0) {
    // 4. Overwrite de @everyone no canal: deny primeiro, allow depois.
    for (const o of overwrites) {
      if (o.targetType === 'role' && o.targetId === guild.id) {
        perms = (perms & ~o.deny) | o.allow;
        break;
      }
    }

    // 5. Overwrites de papéis: ACUMULAR todos os denies, depois todos os
    //    allows. Aplicar papel-a-papel tornaria o resultado dependente da
    //    ordem de iteração — dois membros com os mesmos papéis acabariam
    //    com permissões diferentes conforme o plano de query.
    let roleDeny = NONE;
    let roleAllow = NONE;
    for (const o of overwrites) {
      if (
        o.targetType === 'role' &&
        o.targetId !== guild.id &&
        member.roleIds.includes(o.targetId)
      ) {
        roleDeny |= o.deny;
        roleAllow |= o.allow;
      }
    }
    perms = (perms & ~roleDeny) | roleAllow;

    // 6. Overwrite específico do membro — a palavra final.
    for (const o of overwrites) {
      if (o.targetType === 'member' && o.targetId === member.userId) {
        perms = (perms & ~o.deny) | o.allow;
        break;
      }
    }
  }

  // Sem VIEW_CHANNEL, nada mais do canal importa.
  if ((perms & P.VIEW_CHANNEL) === 0n) {
    perms &= ~CHANNEL_SCOPED_MASK;
  }

  return applyTimeout(perms, member);
}

function applyTimeout(perms: bigint, member: MemberLike): bigint {
  if (member.timeoutUntil && member.timeoutUntil.getTime() > Date.now()) {
    return perms & TIMEOUT_ALLOWED_MASK;
  }
  return perms;
}

// ─── Hierarquia — ortogonal ao bitfield ──────────────────────────────────────

/** Posição do papel mais alto do membro. O dono é efectivamente +∞. */
export function highestPosition(
  guild: GuildLike,
  member: MemberLike,
  roles: ReadonlyMap<bigint, RoleLike>,
): number {
  if (member.userId === guild.ownerId) return Number.POSITIVE_INFINITY;
  let highest = 0;
  for (const roleId of member.roleIds) {
    const pos = roles.get(roleId)?.position ?? 0;
    if (pos > highest) highest = pos;
  }
  return highest;
}

export function canManageRole(
  guild: GuildLike,
  actor: MemberLike,
  role: RoleLike,
  roles: ReadonlyMap<bigint, RoleLike>,
): boolean {
  if (actor.userId === guild.ownerId) return true;
  if (!has(resolvePermissions(guild, actor, roles), P.MANAGE_ROLES)) return false;
  return highestPosition(guild, actor, roles) > role.position;
}

export function canActOnMember(
  guild: GuildLike,
  actor: MemberLike,
  target: MemberLike,
  roles: ReadonlyMap<bigint, RoleLike>,
): boolean {
  if (actor.userId === guild.ownerId) return true;
  if (target.userId === guild.ownerId) return false;
  return highestPosition(guild, actor, roles) > highestPosition(guild, target, roles);
}

/**
 * Não podes conceder num overwrite uma permissão que tu próprio não tenhas.
 *
 * Sem esta verificação, MANAGE_ROLES num único canal é escalada de
 * privilégios em dois passos: concedes-te ADMINISTRATOR por overwrite. É a
 * verificação que falta com mais frequência em implementações caseiras.
 */
export function canGrantOverwrite(
  actorPermissions: bigint,
  next: Pick<Overwrite, 'allow' | 'deny'>,
  previous?: Pick<Overwrite, 'allow' | 'deny'>,
): boolean {
  if (has(actorPermissions, P.ADMINISTRATOR)) return true;
  const changed =
    (next.allow ^ (previous?.allow ?? NONE)) | (next.deny ^ (previous?.deny ?? NONE));
  return (changed & ~actorPermissions) === 0n;
}

// ─── Utilitários ─────────────────────────────────────────────────────────────

export function has(perms: bigint, required: bigint): boolean {
  return (perms & required) === required;
}

export function hasAny(perms: bigint, required: bigint): boolean {
  return (perms & required) !== 0n;
}

export function toNames(perms: bigint): PermissionName[] {
  return (Object.keys(P) as PermissionName[]).filter((name) => (perms & P[name]) !== 0n);
}

export function fromNames(names: readonly PermissionName[]): bigint {
  return names.reduce((acc, name) => acc | P[name], NONE);
}

/** Fronteira: permissões viajam como string decimal, nunca como número. */
export function serialize(perms: bigint): string {
  return perms.toString();
}

export function deserialize(value: string): bigint {
  if (!/^(?:0|[1-9]\d{0,19})$/.test(value)) {
    throw new TypeError(`bitfield de permissões inválido: ${value}`);
  }
  return BigInt(value);
}
